/**
 * A detail page's route, data and canonical move (web plan W0.16).
 *
 * The four detail routes accept an ObjectId or a slug as `:ref`, un-prefixed
 * (`/brand/{ref}`) and under every launched locale (`/pk/en/brand/{ref}`); the
 * locale comes from the URL. The data comes from the type's detail hook
 * (@/hooks/useAppData, passed in by the page), which resolves a slug through
 * `resolveEntityPath`.
 *
 * When the page is not at its canonical path it moves there with
 * `<Navigate replace>` (`redirect`), keeping the query and fragment:
 *  1. the resolver's `canonicalPath` (a 301, or a 200 answered for another
 *     path: an old slug, another case, a slug without its prefix);
 *  2. else the loaded entity's `urlPath` (an id URL once the entity has a
 *     slug path, an id under a prefix while it has none).
 * A fallback only: the SEO server answers real 301s (plan W1.3). Only the
 * backend's paths count — without `urlPath` nothing moves. While a move is
 * pending the status reads `loading`, so the page shows its skeleton, keeps
 * robots and counts no view. Each move is counted in the history entry's
 * state and a visit stops moving after MAX_DETAIL_REDIRECTS, so two answers
 * that disagree (a stale cached resolution, an old slug pointing back) can
 * never loop.
 *
 * `redirect` keeps its identity while its target, the query, the fragment and
 * the history state stay the same: `<Navigate>` re-runs its navigation
 * whenever its `state` prop changes identity, and the entity routes'
 * `clientLoader` keeps that navigation pending across renders, so a fresh
 * object per render restarted it on every render (a render loop, a query
 * storm and a page stuck at the id URL).
 */
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { To } from 'react-router-dom';
import type { DetailState } from '@/hooks/useAppData';
import { useEntityPreload } from '@/hooks/entityPreload';
import type { Locale } from '@/seo/locales';
import { splitLocalePath, type EntityRef } from '@/seo/urls';

/** How many canonical moves one visit may make; one is all a consistent answer needs. */
export const MAX_DETAIL_REDIRECTS = 2;

/** The history state a canonical move leaves on its target. */
export interface DetailRedirectState {
  entityRedirects: number;
}

export interface DetailRedirect {
  to: To;
  state: DetailRedirectState & Record<string, unknown>;
}

export interface EntityPageState<T> extends DetailState<T> {
  /** Where the page must go instead (`<Navigate replace>`), or null. */
  redirect: DetailRedirect | null;
}

/** A path on this site (never an absolute or protocol-relative URL), or null. */
function sitePath(path: string | null | undefined): string | null {
  const value = path?.trim();
  // Never protocol-relative: `//host` or `/\host` (browsers read both as a host).
  return value && /^\/(?![/\\])/.test(value) ? value : null;
}

/** Paths compare exactly, ignoring a trailing slash the router ignores too. */
const pathKey = (path: string): string => path.replace(/(.)\/+$/, '$1');

/**
 * Where a page at `pathname` must move for its detail state (see the module
 * comment), or null. Pure, for the tests.
 */
export function canonicalTarget(
  pathname: string,
  detail: Pick<DetailState<EntityRef>, 'status' | 'item' | 'resolvedPath'>,
): string | null {
  const here = pathKey(pathname);
  const resolved = sitePath(detail.resolvedPath);
  if (resolved && pathKey(resolved) !== here) return resolved;
  if (detail.status === 'ok' && detail.item) {
    const own = sitePath(detail.item.urlPath);
    if (own && pathKey(own) !== here) return own;
  }
  return null;
}

function redirectCount(state: unknown): number {
  const count = (state as Partial<DetailRedirectState> | null)?.entityRedirects;
  return typeof count === 'number' && Number.isFinite(count) ? count : 0;
}

/**
 * `useEntityPage(useBrandDetail)`: the page passes its type's detail hook
 * from @/hooks/useAppData (so tests that mock that module drive it).
 */
export function useEntityPage<T extends EntityRef>(
  useDetail: (ref: string, locale?: Locale | null) => DetailState<T>,
): EntityPageState<T> {
  const { ref = '' } = useParams<{ ref: string }>();
  const location = useLocation();
  const locale = splitLocalePath(location.pathname)?.locale ?? null;
  // An entity route's server copy (web plan W4.4, @/hooks/entityPreload):
  // shown at once, server and hydration alike; the detail queries run only
  // for a signed-in viewer's own copy (an empty key sends nothing).
  const preload = useEntityPreload();
  const detail = useDetail(preload && !preload.refresh ? '' : ref, locale);
  // The server already answered for this exact URL (status, canonical):
  // nothing moves, nothing is retried.
  const showPreload = preload !== null && !(detail.status === 'ok' && detail.item);

  const hops = redirectCount(location.state);
  const target =
    !showPreload && hops < MAX_DETAIL_REDIRECTS ? canonicalTarget(location.pathname, detail) : null;
  const { search, hash, state: historyState } = location;
  const redirect = useMemo<DetailRedirect | null>(() => {
    if (!target) return null;
    const carried = historyState && typeof historyState === 'object' ? (historyState as Record<string, unknown>) : {};
    return {
      to: { pathname: target, search, hash },
      state: { ...carried, entityRedirects: redirectCount(historyState) + 1 },
    };
  }, [target, search, hash, historyState]);

  if (showPreload) {
    return {
      item: preload.item as T,
      status: 'ok',
      loading: false,
      error: null,
      errorCode: null,
      refetch: detail.refetch,
      resolvedPath: null,
      redirect: null,
    };
  }
  if (!redirect) return { ...detail, redirect: null };
  return { ...detail, status: 'loading', redirect };
}
